import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Toggle } from "../toggle.tsx";
import Separator from "../separator";
import { formatPriceNOK } from "../../../utilities/conversions.js";

export function InvoiceModal({
    show,
    onClose,
    baseUrl,
    form,
    setForm,
}) {

    const [sendEmail, setSendEmail] = useState(true);
    const subtotal = useMemo(() => (form.fastpris ? form.unitPrice : form.unitPrice * form.qty), [form]);
    const vatAmount = useMemo(() => (subtotal * form.vat) / 100, [subtotal, form.vat]);
    const total = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                        >
                            ✕
                        </button>

                        <div className="py-4">
                            <h2 className="text-xl font-bold mb-4 px-4">Opprett faktura</h2>
                            <div className="grid gap-4 md:grid-cols-2 px-4 text-sm">
                                <div className="space-y-2">
                                    <label>Kunde</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Kundenavn" value={form.customer} onChange={e => setForm({ ...form, customer: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label>E-post</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="kunde@domene.no" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label>Org.nr (valgfritt)</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="999 999 999" value={form.org} onChange={e => setForm({ ...form, org: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label>Forfallsdager</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" type="number" value={form.dueDays} onChange={e => setForm({ ...form, dueDays: Number(e.target.value) })} />
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label>Beskrivelse</label>
                                    <textarea className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" rows={3} placeholder="Arbeidsbeskrivelse / referanse" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <label>Timepris / fastpris (NOK)</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" type="number" value={form.unitPrice} onChange={e => setForm({ ...form, unitPrice: Number(e.target.value) })} />
                                </div>
                                <div className="space-y-2">
                                    <label>Timer / mengde</label>
                                    <input className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" type="number" value={form.qty} onChange={e => setForm({ ...form, qty: Number(e.target.value) })} disabled={form.fastpris} />
                                </div>
                                <div className="space-y-2">
                                    <label>MVA</label>
                                    <select className="mt-1 w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-purple-500" value={String(form.vat)} onValueChange={v => setForm({ ...form, vat: Number(v) })}>
                                        <option value="0">0 %</option>
                                        <option value="12">12 %</option>
                                        <option value="25">25 %</option>
                                    </select>
                                </div>
                                <div className="space-y-2 flex items-center gap-3 pt-6">
                                    <Toggle label="Video" checked={form.fastpris} onChange={(v) => setForm({ ...form, fastpris: !!v })} id="fastpris" />

                                    <label htmlFor="fastpris">Fastpris (ignorer mengde)</label>
                                </div>
                            </div>

                            <div className="mt-4 rounded-lg border p-4 text-sm mx-4">
                                <div className="flex items-center justify-between"><span>Delsum</span><span className="font-semibold">{formatPriceNOK(subtotal)}</span></div>
                                <div className="flex items-center justify-between"><span>MVA ({form.vat}%)</span><span className="font-semibold">{formatPriceNOK(vatAmount)}</span></div>
                                <Separator className="my-2" />
                                <div className="flex items-center justify-between text-base"><span>Totalt å betale</span><span className="font-bold">{formatPriceNOK(total)}</span></div>
                            </div>

                            <div className="mt-3 flex items-center gap-3 text-sm px-4">
                                <Toggle checked={sendEmail} onCheckedChange={setSendEmail} id="sendEmail" />
                                <label htmlFor="sendEmail">Send faktura som e‑post med betalingslenke (Stripe/Vipps)</label>
                            </div>
                            <div className="flex justify-center">
                                <button class="px-10 py-4 mx-4 rounded-full bg-purple-600 text-white text-lg font-semibold hover:bg-purple-700 transition mt-4 justify-content-center">Opprett Faktura</button>
                            </div>

                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
